import { useState, useEffect, useCallback, useRef } from "react";
import { InfoClient } from "../sdk";
import { executeWithRetry, DefaultRetryConfigs } from "../sdk/utils/retry";
import { useSubscription } from "../context/SubscriptionContext";
import { Position, PositionsConfig, transformPosition } from "../sdk/types/data";
import { MultiFeedPositionsData } from "../sdk/types/api";

/**
 * Hook for managing positions data.
 * Uses WebSocket (user_updates channel) with optional initial REST fetch.
 */
export function usePositions(config: PositionsConfig) {
  const useWebSocket = config.useWebSocket !== false; // Default: true
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const infoClientRef = useRef<InfoClient | null>(null);
  const refreshIntervalRef = useRef<number>(0);
  // Store config in ref to avoid dependency issues in callbacks
  const configRef = useRef(config);
  configRef.current = config;

  /**
   * Fetch positions from API with retry logic
   * Only updates state if data has actually changed to prevent unnecessary re-renders
   * Uses configRef to access latest config without causing dependency loops
   */
  const fetchPositions = useCallback(async () => {
    if (!infoClientRef.current) return;

    const currentConfig = configRef.current;
    // If walletAddress is not provided, we can't fetch positions
    // The API requires an address parameter
    // Don't set error state if address is simply not yet loaded (empty string)
    // Only set error if we've attempted to fetch and it failed
    if (
      !currentConfig.walletAddress ||
      currentConfig.walletAddress.trim() === ""
    ) {
      // Clear error and positions when address is not available
      setError("");
      setPositions([]);
      setLoading(false);
      return;
    }

    setError("");

    try {
      const data = await executeWithRetry(DefaultRetryConfigs.http, () =>
        infoClientRef.current!.getPositions(currentConfig.walletAddress!),
      );

      if (Array.isArray(data)) {
        const transformedPositions: Position[] = data.map(transformPosition);
        // Compare with current positions to avoid unnecessary state updates
        setPositions((prev) => {
          if (prev.length !== transformedPositions.length) {
            return transformedPositions;
          }
          // Deep comparison - only update if positions actually changed
          const prevStr = JSON.stringify(prev);
          const newStr = JSON.stringify(transformedPositions);
          return prevStr === newStr ? prev : transformedPositions;
        });
      } else {
        setPositions((prev) => (prev.length === 0 ? prev : []));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load positions");
      console.error("Failed to fetch positions after retries:", err);
    } finally {
      setLoading(false);
    }
  }, []); // transformPosition is now a pure function imported from data.ts

  /**
   * Handle position updates from WebSocket (multi-feed positions sub-channel)
   * Backend sends position data via multi-feed with typed MultiFeedPositionsData
   * Only updates state if data has actually changed to prevent unnecessary re-renders
   */
  const handleUserUpdates = useCallback(
    (data: unknown) => {
      const payload = data as MultiFeedPositionsData;
      // Multi-feed positions data structure: { marketIndex, address, symbol, ... }
      // Process updates even when symbol is missing; Position component resolves from market definitions
      if (payload.marketIndex) {
        const transformedPosition = transformPosition(payload);
        setPositions((prev) => {
          // Find existing position by positionId (preferred) or market_id (fallback)
          const existingIndex = prev.findIndex(
            (p) => p.positionId === transformedPosition.positionId,
          );
          if (existingIndex >= 0) {
            // Update existing position
            const updated = [...prev];
            updated[existingIndex] = transformedPosition;
            // Deep comparison
            const prevStr = JSON.stringify(prev);
            const newStr = JSON.stringify(updated);
            return prevStr === newStr ? prev : updated;
          } else {
            // Add new position
            return [...prev, transformedPosition];
          }
        });
      }
    },
    [], // transformPosition is now a pure function imported from data.ts
  );

  const { subscribe, unsubscribe, isConnected: contextConnected } = useSubscription();

  // Initialize clients and set up WebSocket (key-based) or polling
  useEffect(() => {
    const infoClient = new InfoClient({
      baseURL: config.baseURL,
      sessionToken: config.authToken,
    });
    infoClientRef.current = infoClient;

    fetchPositions();
    setIsConnected(contextConnected);

    const marketId = config.marketId ?? "0";
    const key = `positions:${marketId}`;
    const callback = (data: unknown) => handleUserUpdates(data);

    if (useWebSocket) {
      subscribe(key, callback);
      if (!contextConnected && config.refreshInterval && config.refreshInterval > 0) {
        refreshIntervalRef.current = window.setInterval(
          () => fetchPositions(),
          config.refreshInterval,
        );
      }
      return () => {
        if (refreshIntervalRef.current !== null && refreshIntervalRef.current !== 0) {
          clearInterval(refreshIntervalRef.current);
          refreshIntervalRef.current = 0;
        }
        unsubscribe(key, callback);
      };
    }

    if (config.refreshInterval && config.refreshInterval > 0) {
      refreshIntervalRef.current = window.setInterval(
        () => fetchPositions(),
        config.refreshInterval,
      );
    }
    return () => {
      if (refreshIntervalRef.current !== null && refreshIntervalRef.current !== 0) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = 0;
      }
    };
  }, [
    config.baseURL,
    config.authToken,
    config.walletAddress,
    config.refreshInterval,
    config.marketId,
    useWebSocket,
    contextConnected,
    subscribe,
    unsubscribe,
    fetchPositions,
    handleUserUpdates,
  ]);

  return {
    positions,
    loading,
    error,
    isConnected,
    refresh: fetchPositions,
  };
}
